/** @file The camera a project opens with: its setup skeleton framed at the viewport's first real size. */
import { docSetupWorlds } from '@pose2d/core/pose/docSetupWorlds';
import { viewCamera, type ViewCamera } from '@pose2d/editor-geometry/viewCamera';
import type { Doc } from '@pose2d/format/types';
import { cameraStore } from '../../stores/cameraStore';
import type { CameraSlice } from '../../stores/types';

const PAD_PX = 48;
const MIN_HALF = 32;

/** The opened document and the camera object `arm` stored; any other camera write (a pan, a zoom, a restored view) replaces that object. */
let opening: { doc: Doc; camera: CameraSlice } | null = null;
/** The mounted viewport's last non-empty size; null while none is mounted or it is hidden (0 × 0). */
let size: readonly [number, number] | null = null;

/** [lo, hi] of one axis, grown around its centre to at least 2 * MIN_HALF (as the Frame action does). */
function span(values: number[]): [number, number] {
  const lo = Math.min(...values), hi = Math.max(...values), mid = (lo + hi) / 2, half = Math.max((hi - lo) / 2, MIN_HALF);
  return [mid - half, mid + half];
}

/** Frames every bone's joint and tip at setup; null for a document without bones. */
function bonesFit(doc: Doc, width: number, height: number): ViewCamera | null {
  const ids = Object.keys(doc.bones);
  if (ids.length === 0) return null;
  const worlds = docSetupWorlds(doc);
  const xs: number[] = [];
  const ys: number[] = [];
  for (const id of ids) {
    const [a = 1, , c = 0, , tx = 0, ty = 0] = worlds.get(id) ?? [];
    const length = doc.bones[id]?.length ?? 0;
    xs.push(tx, tx + a * length);
    ys.push(ty, ty + c * length);
  }
  const [x0, x1] = span(xs);
  const [y0, y1] = span(ys);
  return viewCamera.fit([x0, y0, x1, y1], width, height, PAD_PX);
}

/** Opening never fails on the fit: a document without bones, or a setup chain docSetupWorlds refuses, gives null. */
function framed(doc: Doc, width: number, height: number): ViewCamera | null {
  try { return bonesFit(doc, width, height); } catch { return null; }
}

/** Frames the armed document once the size is known, unless the camera changed since `arm`; then nothing is armed. */
function settle(): void {
  if (opening === null || size === null) return;
  const { doc, camera } = opening;
  opening = null;
  const fitted = cameraStore.getState() === camera ? framed(doc, size[0], size[1]) : null;
  if (fitted !== null) cameraStore.setState(fitted, true);
}

/**
 * The editor opens on the whole skeleton (the Frame action's result with nothing selected). The document usually
 * arrives before the viewport is mounted, so loadProject `arm`s the fit and the viewport's size observer calls
 * `sized`; a document that arrives while a sized viewport is mounted (a second load) is framed at once.
 */
export const openingFit = {
  /** The camera framing every setup joint and tip of `doc` in a width × height viewport; null when it cannot be framed. */
  fit: framed,
  /** A project was opened: the origin camera, then `doc` framed at the viewport's real size (now, when known). */
  arm(doc: Doc): void {
    const camera = { ...cameraStore.getInitialState() };
    cameraStore.setState(camera, true);
    opening = { doc, camera };
    settle();
  },
  /** The viewport's size (0 × 0 when hidden or unmounted): the first real one frames the armed document. */
  sized(width: number, height: number): void {
    size = width > 0 && height > 0 ? [width, height] : null;
    settle();
  },
};
